import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getPopularBrokers, getNews, NEWS_FALLBACK } from "../lib/api";
import { BrokerLogo } from "../lib/logo.jsx";
import { Seo } from "../components/Seo";
import { ArrowUpRight, ArrowRight, Search, Building2, Newspaper, Scale } from "lucide-react";

const MIDAS_URL = "https://www.getmidas.com";
const shortName = (name) => name.replace(/MENKUL.*/i, "").replace("A.Ş.", "").trim();
const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

const ease = [0.22, 1, 0.36, 1];
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease } } };

const Home = () => {
  const [popular, setPopular] = useState([]);
  const [news, setNews] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    getPopularBrokers().then((d) => setPopular(d.brokers)).catch(() => {});
    getNews(5).then((d) => setNews(d.news)).catch(() => {});
  }, []);

  const lead = news[0];
  const rest = news.slice(1, 5);

  return (
    <>
      <Seo
        title="SPK Aracı Kurumlar | Borsa Aracı Kurum Rehberi & Güncel Haberler"
        description="69 SPK lisanslı aracı kurumun iletişim bilgileri, güncel borsa haberleri ve karşılaştırması. Midas'ta dakikalar içinde ücretsiz hesap açın."
        path="/"
      />

      {/* HERO — gazete manşeti */}
      <section className="border-b-2 border-ink bg-background paper-texture">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 py-14 lg:grid-cols-12 lg:py-16">
          <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7 lg:border-r lg:border-hair lg:pr-10">
            <motion.span variants={fadeUp} className="kicker inline-block border-b-2 border-claret pb-1 text-claret">
              SPK Lisanslı · 69 Aracı Kurum
            </motion.span>
            <motion.h1 variants={fadeUp} className="mt-5 font-display text-[44px] font-semibold leading-[1.02] tracking-[-0.01em] text-ink sm:text-6xl">
              Doğru aracı kurumu <span className="italic text-claret">güvenle</span> seçin.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 max-w-xl font-display text-lg leading-relaxed text-muted-foreground">
              Borsa İstanbul yetkili aracı kurumlarının iletişim bilgileri, güncel borsa
              haberleri ve karşılaştırması — tek, bağımsız bir rehberde.
            </motion.p>

            <motion.form variants={fadeUp} action="/araci-kurumlar" className="mt-7 flex max-w-xl items-center gap-2 border-b-2 border-ink pb-1" data-testid="hero-search-form">
              <Search size={18} className="text-ink" />
              <input
                name="q"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Aracı kurum adı veya telefon no ile arayın..."
                className="h-9 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-muted-foreground"
                data-testid="hero-search-input"
              />
              <Link to={`/araci-kurumlar${query ? `?q=${encodeURIComponent(query)}` : ""}`} className="rounded-sm bg-ink px-4 py-1.5 text-sm font-semibold text-paper transition-colors hover:bg-claret" data-testid="hero-search-button">
                Ara
              </Link>
            </motion.form>

            <motion.div variants={fadeUp} className="mt-7 flex flex-wrap items-center gap-3">
              <motion.a whileHover={{ y: -2 }} href={MIDAS_URL} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-sm bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-claret"
                data-testid="hero-open-account-button">
                Midas'ta Hesap Aç <ArrowUpRight size={16} />
              </motion.a>
              <Link to="/araci-kurumlar" className="group inline-flex items-center gap-1.5 border-b-2 border-transparent py-1 text-sm font-semibold text-ink transition-colors hover:border-claret" data-testid="hero-all-brokers-button">
                Tüm Kurumları Gör <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Lead haber */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease, delay: 0.1 }} className="lg:col-span-5">
            {lead ? (
              <Link to={`/borsa-haberleri/${lead.id}`} className="group block" data-testid="hero-lead-news">
                <div className="overflow-hidden border border-ink">
                  <img src={lead.image_url} alt={lead.title} referrerPolicy="no-referrer" onError={(e) => { if (e.currentTarget.src !== NEWS_FALLBACK) e.currentTarget.src = NEWS_FALLBACK; }} className="aspect-[16/10] w-full object-cover saturate-[0.9] transition duration-700 group-hover:scale-[1.04]" />
                </div>
                <span className="kicker mt-3 block text-claret">Manşet · {lead.source}</span>
                <h3 className="mt-1.5 font-display text-2xl font-semibold leading-snug text-ink transition-colors group-hover:text-claret">{lead.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-3">{lead.summary}</p>
              </Link>
            ) : (
              <div className="flex h-full min-h-[260px] items-center justify-center border border-dashed border-hair p-8 text-center text-sm text-muted-foreground">
                Güncel borsa haberleri her gün otomatik yayınlanır.
              </div>
            )}
          </motion.div>
        </div>

        {/* Künye şeridi */}
        <div className="border-t border-ink bg-ink text-paper">
          <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-paper/15 px-4 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-6">
            {[
              { icon: Building2, t: "69 SPK Lisanslı Kurum", s: "Güncel iletişim bilgileri" },
              { icon: Newspaper, t: "Günde 2 Kez Haber", s: "Otomatik güncellenen gündem" },
              { icon: Scale, t: "Bağımsız & Ücretsiz", s: "Tarafsız rehber bilgisi" },
            ].map((x) => (
              <div key={x.t} className="flex items-center gap-3 py-5 sm:px-6">
                <x.icon size={20} className="text-claret-soft" />
                <div>
                  <div className="text-sm font-semibold">{x.t}</div>
                  <div className="text-xs text-paper/60">{x.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR 5 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between gap-4 border-b border-ink pb-4">
          <div>
            <span className="kicker text-claret">Öne Çıkanlar</span>
            <h2 className="mt-2 font-display text-3xl font-semibold text-ink lg:text-4xl">En Popüler 5 Aracı Kurum</h2>
          </div>
          <Link to="/araci-kurumlar" className="hidden items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink sm:inline-flex">
            Tümü <ArrowRight size={15} />
          </Link>
        </div>

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}
          className="mt-8 grid border-l border-t border-hair sm:grid-cols-2 lg:grid-cols-3">
          {popular.map((b) => (
            <motion.div key={b.slug} variants={fadeUp}
              className="group relative flex flex-col border-b border-r border-hair bg-card p-6 transition-colors hover:bg-tint"
              data-testid={`popular-broker-${b.slug}`}>
              {b.badge && (
                <span className="kicker absolute right-5 top-5 bg-claret px-2 py-1 text-[9px] text-paper">{b.badge}</span>
              )}
              <div className="flex items-center gap-4">
                <BrokerLogo slug={b.slug} name={b.name} size={54} />
                <div className="min-w-0">
                  <div className="font-display text-lg font-semibold text-ink">{shortName(b.name)}</div>
                  <div className="text-xs text-muted-foreground">{b.tagline}</div>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <a href={b.cta_url} target="_blank" rel="noopener noreferrer"
                  className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${
                    b.badge ? "bg-ink text-paper hover:bg-claret" : "border border-ink text-ink hover:bg-ink hover:text-paper"
                  }`}
                  data-testid={`popular-cta-${b.slug}`}>
                  {b.cta_text} <ArrowUpRight size={14} />
                </a>
                <Link to={`/kurum/${b.slug}`} className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-claret hover:underline" data-testid={`popular-detail-${b.slug}`}>
                  Detay
                </Link>
              </div>
            </motion.div>
          ))}
          <div className="hidden border-b border-r border-hair bg-card lg:block" />
        </motion.div>
      </section>

      {/* NEWS */}
      {rest.length > 0 && (
        <section className="border-y border-ink bg-tint/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
            <div className="flex items-end justify-between gap-4 border-b border-ink pb-4">
              <div>
                <span className="kicker text-claret">Gündem</span>
                <h2 className="mt-2 font-display text-3xl font-semibold text-ink lg:text-4xl">Borsa & Piyasa Haberleri</h2>
              </div>
              <Link to="/borsa-haberleri" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink" data-testid="all-news-link">
                Tümü <ArrowRight size={15} />
              </Link>
            </div>

            <div className="mt-8 grid gap-x-10 gap-y-8 md:grid-cols-2">
              {rest.map((n, i) => (
                <motion.div key={n.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease, delay: (i % 2) * 0.08 }}>
                  <Link to={`/borsa-haberleri/${n.id}`} className="group flex gap-5 border-b border-hair pb-8" data-testid={`news-card-${n.id}`}>
                    <div className="h-24 w-32 shrink-0 overflow-hidden border border-hair">
                      <img src={n.image_url} alt={n.title} loading="lazy" referrerPolicy="no-referrer" onError={(e) => { if (e.currentTarget.src !== NEWS_FALLBACK) e.currentTarget.src = NEWS_FALLBACK; }} className="h-full w-full object-cover saturate-[0.9] transition duration-700 group-hover:scale-105" />
                    </div>
                    <div className="min-w-0">
                      <span className="kicker text-claret">{n.source}</span>
                      <h3 className="mt-1 font-display text-lg font-semibold leading-snug text-ink line-clamp-2 transition-colors group-hover:text-claret">{n.title}</h3>
                      <span className="mt-2 block text-xs text-muted-foreground">{formatDate(n.published_at)}</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }}
          className="flex flex-col items-start justify-between gap-6 border-2 border-ink bg-card p-8 sm:flex-row sm:items-center sm:p-12">
          <div>
            <h2 className="font-display text-3xl font-semibold text-ink">Dakikalar içinde ücretsiz hesap açın</h2>
            <p className="mt-2 text-sm text-muted-foreground">Düşük komisyon, hızlı onay. Midas ile yatırıma hemen başlayın.</p>
          </div>
          <motion.a whileHover={{ y: -2 }} href={MIDAS_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-sm bg-ink px-7 py-3.5 text-sm font-semibold text-paper transition-colors hover:bg-claret"
            data-testid="cta-band-open-account">
            Midas'ta Hesap Aç <ArrowUpRight size={16} />
          </motion.a>
        </motion.div>
      </section>
    </>
  );
};

export default Home;
